"use client";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { Option } from "@/types/Common";
import { normalizeText } from "@/utils/normalizeText";
import { Portal } from "@radix-ui/react-portal";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

interface FilteredSelectProps {
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
  options: Option[];

  onEnter?: (raw: string) => void;

  onSelect?: () => void;

  textEmpty?: string;
}

export function FilteredSelectLocation({
  value,
  onChange,
  placeholder,
  options,
  onSelect,
}: FilteredSelectProps) {
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const commandListRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // mantém input sincronizado com o value externo
  useEffect(() => {
    if (!isFocused) {
      if (value) {
        const selectedOption = options.find((o) => o.value === value);
        setInputValue(selectedOption ? selectedOption.label : "");
      } else {
        setInputValue("");
      }
    }
  }, [value, options, isFocused]);

  const filtered = useMemo(() => {
    const inputNorm = normalizeText(inputValue);
    return options
      .filter((o) => normalizeText(o.label).includes(inputNorm))
      .slice(0, 300);
  }, [options, inputValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // no modo somente-lista, limpar o input limpa o value externo
    if (newValue === "") {
      onChange("");
    }
  };

  const commitSelection = (optionValue: string) => {
    const selectedOption = options.find((o) => o.value === optionValue);
    if (!selectedOption) return;

    onChange(selectedOption.value);
    setInputValue(selectedOption.label);

    setIsFocused(false);
    inputRef.current?.blur();
    onSelect?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();

      // Somente aceita se o texto bater **exatamente** com algum label (normalizado)
      const matchExact = options.find(
        (o) => normalizeText(o.label) === normalizeText(inputValue.trim())
      );

      if (matchExact) {
        commitSelection(matchExact.value);
      }
      // Caso contrário, não faz nada — força o usuário a clicar/selecionar da lista
    } else if (e.key === "Escape") {
      setIsFocused(false);
      inputRef.current?.blur();
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInputValue("");
    onChange("");
    inputRef.current?.focus();
  };

  // Recalcula posição do dropdown
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (commandListRef.current && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const vv = window.visualViewport;

        const top = rect.bottom + 4 + (vv ? vv.offsetTop : 0);
        const left = rect.left + (vv ? vv.offsetLeft : 0);

        commandListRef.current.style.width = `${rect.width}px`;
        commandListRef.current.style.left = `${left}px`;
        commandListRef.current.style.top = `${top}px`;
      }
    };

    const handleScroll = () => {
      if (isFocused) updateDropdownPosition();
    };
    const handleResize = () => {
      if (isFocused) updateDropdownPosition();
    };

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener("scroll", handleResize);

    if (isFocused) updateDropdownPosition();

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("scroll", handleResize);
    };
  }, [isFocused]);

  // Fecha ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        commandListRef.current &&
        !commandListRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    if (isFocused) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFocused]);

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full pr-16"
          aria-autocomplete="list"
          aria-expanded={isFocused}
          aria-controls="location-command-list"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded-full hover:bg-muted"
              aria-label="Limpar"
              title="Limpar"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setIsFocused((prev) => {
                const next = !prev;
                if (next) inputRef.current?.focus();
                else inputRef.current?.blur();
                return next;
              });
            }}
            className="p-1 rounded-full hover:bg-muted"
            aria-label={isFocused ? "Fechar opções" : "Abrir opções"}
            title={isFocused ? "Fechar opções" : "Abrir opções"}
          >
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      </div>

      {isFocused && (
        <Portal>
          <div
            ref={commandListRef}
            className={cn(
              "fixed z-50 bg-popover text-popover-foreground shadow-md rounded-md overflow-hidden",
              isDesktop ? "max-h-[300px]" : "max-h-[200px]"
            )}
            style={{
              width: containerRef.current?.getBoundingClientRect().width + "px",
              top:
                (containerRef.current?.getBoundingClientRect().bottom || 0) +
                4 +
                "px",
              left: containerRef.current?.getBoundingClientRect().left + "px",
            }}
          >
            <Command>
              <CommandList
                id="location-command-list"
                className="max-h-full overflow-auto"
              >
                <CommandEmpty>
                  <div className="px-4 py-3 text-xs text-n-text-neutral">
                    Nenhum resultado. Selecione um município da <b>lista</b>.
                  </div>
                </CommandEmpty>

                <CommandGroup>
                  {filtered.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => commitSelection(option.value)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        </Portal>
      )}
    </div>
  );
}
